import type { PanoApi } from '../pano/index';
import type { PlayerApi } from '../replay/player';
import type { ReplayApi } from '../replay/types';
import type { TourApi } from '../tour/index';
import type { Annotator as AnnotatorClass, loadImage as loadImageFn } from './annotator';

export interface Renderer {
  toBlob: (node: HTMLElement, opts?: object) => Promise<Blob | null>;
  toCanvas: (node: HTMLElement, opts?: object) => Promise<HTMLCanvasElement>;
  Annotator: typeof AnnotatorClass;
  loadImage: typeof loadImageFn;
}

/**
 * CDN/IIFE build: IIFE bundles cannot code-split, so the heavy parts ship as sibling files injected on
 * demand — `screenshot.js` (renderer + annotator, src/screenshot-entry.ts), `replay.js` (rrweb screen
 * recorder, src/replay-entry.ts), `player.js` (rrweb player, src/player-entry.ts), `tour.js` (guide player, src/tour-entry.ts) and `pano.js` (a yetkili kişi's
 * statistics, src/pano-entry.ts). Each sets one global.
 */
declare global {
  interface Window {
    FETraceScreenshot?: Renderer;
    FETraceReplay?: ReplayApi;
    FETracePlayer?: PlayerApi;
    FETraceTour?: TourApi;
    FETracePano?: PanoApi;
    __fetraceScriptUrl?: string;
  }
}

const loading: Record<string, Promise<unknown>> = {};

function sibling<T>(file: string, key: 'FETraceScreenshot' | 'FETraceReplay' | 'FETracePlayer' | 'FETraceTour' | 'FETracePano'): Promise<T> {
  if (window[key]) return Promise.resolve(window[key] as T);
  return (loading[key] ??= new Promise<T>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = (window.__fetraceScriptUrl ?? '').replace(/[^/]*$/, '') + file;
    s.async = true;
    s.onload = () => (window[key] ? resolve(window[key] as T) : reject(new Error(file + ' missing')));
    s.onerror = () => {
      delete loading[key];
      reject(new Error(file + ' load failed'));
    };
    document.head.appendChild(s);
  })) as Promise<T>;
}

export const loadRenderer = (): Promise<Renderer> => sibling('screenshot.js', 'FETraceScreenshot');
export const loadReplay = (): Promise<ReplayApi> => sibling('replay.js', 'FETraceReplay');
export const loadPlayer = (): Promise<PlayerApi> => sibling('player.js', 'FETracePlayer');
export const loadTour = (): Promise<TourApi> => sibling('tour.js', 'FETraceTour');
export const loadPano = (): Promise<PanoApi> => sibling('pano.js', 'FETracePano');
